import { Box } from "@primer/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import Modal from "../../components/ui/modal";
import { useCommonSelectedHook } from "../../hooks/useCommonSelectedHook";
import { useWindowSize } from "../../hooks/useWindowSize";
import { crmHocSinhApi } from "./../../api/crmHocSinhApi";
import BaoPhiLayout from "./bao-phi";
import CanBoNhanVienLayout from "./canbo-nhanvien/CanBoNhanVienLayout";
import { DangKyDichVu } from "./dich-vu";
import GhiDanhPage from "./ghi-danh";
import QuanHeGiaDinhLayout from "./gia-dinh/QuanHeGiaDinhLayout";
import { GiayToNopForm } from "./giay-to-nop";
import HinhThucMienGiam from "./hinh-thuc-mien-giam";
import { HocSinhProfileListTab } from "./list-tab";
import { hocSinhProfileTabs } from "./list-tab/HocSinhProfileListTab";
import { HocSinhNameAndAvavtar } from "./name-and-avatar";
import ProfileForm from "./profile-form/ProfileForm";
import SucKhoeHocSinhPage from "./suc-khoe";
import { ThiDauVao } from "../thi-dau-vao";
import BHYTPage from "../bhyt/BHYT";
import BHYTForm from "./bhyt/BHYTForm";
import QuaTrinhHocPage from "./qua-trinh-hoc/QuaTrinhHocPage";
import BusPage from "./bus/BusPage";
import BanTruPage from "./ban-tru/BanTruPage";

interface HocSinhProfileProps {
  id: number;
  value?: number;
  height?: number;
  phuHuynh?: any;
  initialTab?: string;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormRef {
  handleSaveChanges: () => void;
}

const HocSinhProfile = (props: HocSinhProfileProps) => {
  const { id } = useParams<{ id: string }>();
  const [hocSinh, setHocSinh] = useState<any>({ id: props.id });
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState<string>(props.initialTab || "profile");
  const [profileKey, setProfileKey] = useState(0);
  const componentDidMount = useRef(false);
  const { nam_hoc, dm_coso_id } = useCommonSelectedHook();
  const { height } = useWindowSize();
  const profileFormRef = useRef<FormRef | null>(null);

  // Stable hocSinhId để tránh nhấp nháy tab
  const stableHocSinhId = useMemo(() => {
    const targetId = props.value !== undefined && props.value >= 0
      ? props.value
      : parseInt(id || "0", 10) || props.id;
    // Đảm bảo ID luôn > 0 để hiển thị đầy đủ tab, trừ khi thực sự là tạo mới
    return targetId > 0 ? targetId : (props.id > 0 ? props.id : 0);
  }, [props.value, id, props.id]);

  // Stable hocSinh object để tránh re-render HocSinhNameAndAvavtar
  const stableHocSinh = useMemo(() => {
    // Chỉ lấy những field cần thiết cho HocSinhNameAndAvavtar
    return {
      id: stableHocSinhId,
      ho_ten: hocSinh.ho_ten || '',
      ma_hs: hocSinh.ma_hs || '',
      ma_tn: hocSinh.ma_tn || '',
      avatar: hocSinh.avatar || '',
      dm_coso_id: hocSinh.dm_coso_id || 0,
      ten_lop: hocSinh.ten_lop || ''
    };
  }, [stableHocSinhId, hocSinh.ho_ten, hocSinh.ma_hs, hocSinh.ma_tn, hocSinh.avatar, hocSinh.dm_coso_id, hocSinh.ten_lop]);

  const handleSave = () => {
    profileFormRef.current?.handleSaveChanges();
  };

  // Effect để xử lý initial hocSinh ID
  useEffect(() => {
    if (props.value !== undefined && props.value >= 0) {
      setHocSinh({ id: props.value });
    } else {
      setHocSinh({ id: parseInt(id || "0", 10) });
    }
  }, [props.value, id]);

  // Effect cho việc load data và mount component
  useEffect(() => {
    componentDidMount.current = true;

    const initialLoad = async () => {
      if (hocSinh.id > 0) {
        await handleGetHocSinhData();
        setProfileKey(prev => prev + 1);
      }
    };

    initialLoad();

    return () => {
      componentDidMount.current = false;
    };
  }, [hocSinh.id]);

  const handleGetHocSinhData = async () => {
    setIsLoading(true);
    try {
      const res = await crmHocSinhApi.getById(hocSinh.id, nam_hoc);
      if (!componentDidMount.current) return;
      if (res.is_success) {
        setHocSinh(res.data);
      }
    } catch (error) {
      console.error("Error fetching student data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabChange = async (tab: string) => {
    setSelectedTab(tab);
    if (tab === "profile" && hocSinh.id > 0) {
      await handleGetHocSinhData();
      setProfileKey(prev => prev + 1);
    }
  };

  const hocSinhProfileTab = useMemo(() => {
    return hocSinhProfileTabs.find((x) => x.id === selectedTab);
  }, [selectedTab]);

  return (
    <Modal
      isOpen
      onClose={props.onClose}
      // width={"90%"}
      sx={{
        width: "1400px"
      }}
      title={"Thông tin học sinh"}
    >
      <Box sx={{
        p: 1,
        overflow: "hidden"
      }}>
        <Box
          className="top-side"
          mb={0}
          pb={1}
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            flexGrow={1}
          >
            <Box position="relative">
              {/* {isLoading && (
                <Box position="absolute" top={0} left={0} right={0} zIndex={1}>
                  <PlaceHolder line_number={2} />
                </Box>
              )} */}
              {stableHocSinh.ho_ten &&
                <Box opacity={isLoading ? 0.3 : 1}>
                  <HocSinhNameAndAvavtar hocSinh={stableHocSinh} />
                </Box>
              }
            </Box>
            <Box flexGrow={1}>
              <HocSinhProfileListTab
                selectedTab={selectedTab}
                hocSinhId={stableHocSinhId}
                onTabSelectionChanged={handleTabChange}
              />
            </Box>
          </Box>
        </Box>

        <Box
          className="bottom-side"
          overflow="auto"
          height={props.height ?? height - 300}
        >
          <Box className="content">
            <Box p={2}>
              {selectedTab === "profile" && (
                <ProfileForm
                  ref={profileFormRef}
                  key={`${hocSinh.id}-${profileKey}`} // Thêm profileKey vào key
                  hocSinh={hocSinh}
                  onSubmitSuccess={(id: any) => {
                    if (hocSinh.id > 0) {
                      handleGetHocSinhData();
                    } else {
                      setHocSinh({ id });
                    }
                    props.onSuccess();
                  }}
                />
              )}
              {selectedTab === "familly" && (
                <QuanHeGiaDinhLayout hocSinh={hocSinh} />
              )}
              {selectedTab === "cbnv" && (
                <CanBoNhanVienLayout hocSinh={hocSinh} />
              )}
              {selectedTab === "thidauvao" && (
                <ThiDauVao hocSinh={hocSinh} />
              )}

              {selectedTab === "health" && (
                <SucKhoeHocSinhPage ts_hocsinh_id={hocSinh.id} />
              )}
              {selectedTab === "discount" && (
                <HinhThucMienGiam hocSinh={hocSinh} />
              )}
              {selectedTab === "service" && (
                <DangKyDichVu hocSinh={hocSinh} />
              )}
              {selectedTab === "registration" && (
                <GhiDanhPage hocSinh={hocSinh} />
              )}
              {selectedTab === "debit_note" && (
                <BaoPhiLayout hocSinh={hocSinh} />
              )}
              {selectedTab === "saleset" && (
                <DangKyDichVu hocSinh={hocSinh} />
              )}
              {selectedTab === "document" && (
                <GiayToNopForm
                  hocSinh={hocSinh}
                  onUploadedAvatar={handleGetHocSinhData}
                />
              )}
              {selectedTab === "bhyt" && (
                <BHYTForm ts_hocsinh_id={hocSinh.id} />
              )}
              {selectedTab === "history" && (
                <QuaTrinhHocPage ts_hocsinh_id={hocSinh.id} />
              )}
              {selectedTab === "bus" && (
                <BusPage ts_hocsinh_id={hocSinh.id} />
              )}
              {selectedTab === "boarding" && (
                <BanTruPage ts_hocsinh_id={hocSinh.id} />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}
export default HocSinhProfile;